import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import { useAuth } from "@/context/AuthContext";
import { useGuruThreads } from "@/hooks/useGuruChat";
import ThreadList from "@/components/guru-chat/ThreadList";
import ChatWindow from "@/components/guru-chat/ChatWindow";
import GuruPublicTeaser from "@/components/guru-chat/GuruPublicTeaser";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const GuruChatPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { threadId } = useParams<{ threadId: string }>();
  const { user, isLoading, isAuthenticated } = useAuth();
  const { threads, loading, createThread, deleteThread, reload } = useGuruThreads();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Bootstrap conversation só para usuários logados.
  useEffect(() => {
    if (!user || loading) return;
    if (!threadId) {
      if (threads.length > 0) {
        navigate(`/guru-chat/${threads[0].id}`, { replace: true });
      } else {
        (async () => {
          const t = await createThread();
          if (t) navigate(`/guru-chat/${t.id}`, { replace: true });
        })();
      }
    }
  }, [user, loading, threadId, threads, createThread, navigate]);

  // Scroll automático para a ferramenta quando a rota tem âncora ou sempre que entrar na página
  useEffect(() => {
    const wantsAnchor = location.hash === "" || location.hash === "#guru-chat-funcao";
    if (!wantsAnchor) return;
    const t = setTimeout(() => {
      anchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(t);
  }, [location.pathname, location.hash, threadId]);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  // Visitante/deslogado → teaser público (sem envio real).
  // Importante: só mostra teaser quando definitivamente não autenticado para evitar
  // "flicker de desconexão" durante navegação enquanto o user está sendo enriquecido.
  if (!isAuthenticated && !user) {
    return <GuruPublicTeaser />;
  }

  const handleCreate = async () => {
    const t = await createThread();
    if (t) {
      navigate(`/guru-chat/${t.id}`);
      setSidebarOpen(false);
    }
  };

  const handleDelete = async (id: string) => {
    await deleteThread(id);
    if (id === threadId) {
      const remaining = threads.filter((t) => t.id !== id);
      if (remaining.length > 0) navigate(`/guru-chat/${remaining[0].id}`, { replace: true });
      else navigate("/guru-chat", { replace: true });
    }
  };

  return (
    <>
      <SEOHead
        title="Guru Chat — Converse com a IA do Palpite Guru"
        description="Chat com a IA do Palpite Guru: interprete sonhos, peça palpites do dia e abra as ferramentas internas com 1 clique."
        canonical="/guru-chat"
      />
      <div className="container mx-auto px-2 md:px-4 py-4 overflow-hidden">
        <div className="mb-3 flex items-center justify-between md:hidden">
          <h1 className="text-lg font-bold text-foreground">Guru Chat</h1>
          <Button size="sm" variant="outline" onClick={() => setSidebarOpen((v) => !v)}>
            {sidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            <span className="ml-1">Conversas</span>
          </Button>
        </div>
        <div
          id="guru-chat-funcao"
          ref={anchorRef}
          className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-3 h-[calc(100vh-180px)] min-h-[500px] rounded-xl border border-border overflow-hidden bg-background scroll-mt-20"
        >
          <div className={`${sidebarOpen ? "block" : "hidden"} md:block min-w-0 min-h-0 overflow-hidden`}>
            <ThreadList
              activeId={threadId ?? null}
              threads={threads}
              onSelect={(id) => { navigate(`/guru-chat/${id}`); setSidebarOpen(false); }}
              onCreate={handleCreate}
              onDelete={handleDelete}
            />
          </div>
          <div className="flex flex-col min-h-0 min-w-0 overflow-hidden">
            {threadId ? (
              <ChatWindow key={threadId} threadId={threadId} onThreadUpdated={reload} />
            ) : (
              <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
                Carregando…
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default GuruChatPage;
